<template>
    <MaltLocationAutocomplete
        ref="locationAutocompleteRef"
        v-model="fullLocationSuggestion"
        hide-location-icon
        hide-clearable-icon
        :label="locationPlaceholder"
        input-test-id="header_search-form__location-input"
        :append-to-self="!isDisplayMobile"
        prevent-option-selection
        :remote-allowed="true"
        :remote-by-country="true"
        :remote-label="remoteCountryLabel ?? undefined"
        :without-border="withoutBorder"
        :layer="[
            {name: 'LOCALITY', size: 5},
            {name: 'COUNTRY', size: 2},
        ]"
        secondary-suggestion-variant
        :pt="pt"
    />
</template>

<script setup lang="ts">
    import {computed, watch} from 'vue';
    import {navbarEventBus} from '@navbar/events';
    import {useHeaderMenuStore} from '@navbar-logged/features/header-menu';
    import {storeToRefs} from 'pinia';
    import {MaltLocationAutocomplete} from '#components';
    import {onBeforeUnmount, onMounted, ref, useTranslation, useGlobalMatching} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {useRemoteLabels} from '../../composables/useRemoteLabels';
    import {useSearchFormStore} from '../../search.store';

    defineProps<{
        withoutBorder?: boolean;
    }>();

    const {isDisplayMobile} = useDisplayMobile();
    const {isBrowsingGlobalMatching} = useGlobalMatching();

    const {t} = useTranslation();
    const locationPlaceholder = computed(() =>
        isBrowsingGlobalMatching.value ? t('search.location.com.label') : t('navbar.header.search.form.location.label'),
    );

    const locationAutocompleteRef = ref<InstanceType<typeof MaltLocationAutocomplete> | null>(null);

    const headerMenuStore = useHeaderMenuStore();
    const searchFormStore = useSearchFormStore();
    const {locationQuery, fullLocationSuggestion} = storeToRefs(searchFormStore);

    const {remoteCountryLabel} = useRemoteLabels();

    // Synchronize locationQuery with fullLocationSuggestion for display in HeaderSearchPreview
    // and Regle validation. The immediate flag ensures the sync happens on mount with URL params.
    watch(
        fullLocationSuggestion,
        (value) => {
            if (value) {
                locationQuery.value = value.formattedAddress || '';
            } else {
                locationQuery.value = '';
            }
        },
        {immediate: true},
    );

    function focusField() {
        locationAutocompleteRef.value?.focus();
    }

    const pt = computed(() => ({
        option: {
            style: {
                fontSize: 'var(--joy-font-size-primary-400) !important',
                padding: 'var(--joy-core-spacing-2) var(--joy-core-spacing-5)',
            },
        },
        overlay: {
            style: {
                borderRadius: 'var(--joy-core-radius-3)',
                boxShadow: 'var(--joy-core-elevation-2)',
                border: 'none !important',
            },
        },
    }));

    onMounted(() => {
        if (headerMenuStore.searchPreviewTarget === 'LOCATION') {
            focusField();
        }
        navbarEventBus.on('focus-search-location-input', focusField);
    });

    onBeforeUnmount(() => {
        navbarEventBus.off('focus-search-location-input', focusField);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    :deep(.p-autocomplete-overlay) {
        margin-top: var(--joy-core-spacing-5) !important;
        @include mq.screen_md {
            margin-top: var(--joy-core-spacing-3) !important;
        }
    }

    :deep(.p-autocomplete.p-inputwrapper) {
        height: 100%;
    }
    :deep(.p-autocomplete .p-inputtext) {
        height: var(--input-height);
        padding-right: var(--joy-core-spacing-10);
    }
    :deep(.p-autocomplete .p-inputtext:not(:focus)) {
        box-shadow: none !important;
    }
    :deep(.p-autocomplete .p-inputtext:focus) {
        background-color: var(--joy-color-neutral-0) !important;
    }

    @include mq.screen_md {
        :deep(.p-autocomplete .p-inputtext:hover) {
            border-color: var(--joy-color-border-hover);
        }
    }
</style>
